// LIVE-VERIFY (REV-21679 / REV-21057): real Microsoft File Picker v8 wiring — only
// verifiable against a live tenant + running app; not unit-tested.
//
// Injectable seam for the Microsoft OneDrive/SharePoint File Picker v8.
//
// The picker is a NET-NEW third-party dependency (Microsoft's hosted picker),
// so it sits behind an abstract class the same way `vendor-adapter.ts` isolates
// the auto-match vendor specifics. The smart container depends ONLY on the
// abstract `SharePointFilePickerService`; the real implementation loads the
// hosted picker at runtime, and tests inject `MockSharePointFilePickerService`.
//
// ALL tokens are BE-minted (see `IntegrationService.getSharepointPickerToken`): this lib
// never runs MSAL and never talks to Microsoft's identity endpoints. That is why the seam
// takes a `connectionId` rather than a token — the picker asks for a token per resource
// (its `authenticate` command carries the resource) and the docs are explicit that Graph
// tokens are NOT accepted: "Currently the control relies on SharePoint tokens and not
// Graph, so you will need to ensure your resource is correct and you cannot reuse tokens
// for Graph calls." So the implementation mints on demand, per requested resource.
//
// Protocol implemented verbatim from
// https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers/
// and the v8 configuration schema at .../file-pickers/v8-schema.

import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { IntegrationService } from './integration.service';

/**
 * A folder the user picked in the Microsoft File Picker. This is the RAW picker
 * output shape — the BE turns it into a persisted `SelectedClosingFolder`. Kept
 * deliberately minimal (only what the add-folders call needs).
 */
export interface PickedSharepointFolder {
    /** The Microsoft Graph drive the folder lives in. */
    driveId: string;
    /** The Graph item id of the folder itself. */
    folderItemId: string;
    /** Human-readable path, shown in the folder list until the BE re-resolves it. */
    folderPath: string;
    /** Whether nested folders should be included when scanning for documents. */
    includeSubfolders: boolean;
    /** Which Microsoft surface the folder came from. */
    source: 'sharepoint' | 'onedrive';
}

/** Options accepted by {@link SharePointFilePickerService.openPicker}. */
export interface OpenPickerOptions {
    /** Pre-select nested-folder inclusion (defaults to true — closings usually want subfolders). */
    includeSubfolders?: boolean;
    /**
     * The TENANT's SharePoint Online origin (e.g. `https://contoso.sharepoint.com`), i.e.
     * the picker's `{baseUrl}`. REQUIRED for the real implementation — it comes from the BE
     * picker-token response (`baseUrl`). There is deliberately NO default: guessing a host
     * would open a picker against a foreign tenant.
     */
    baseUrl?: string | null;
    /**
     * An ALREADY-OPENED popup window to drive, instead of opening one here (REV-21057 BUG 2).
     *
     * `window.open` is only allowed inside the synchronous call stack of a user gesture.
     * Awaiting the picker token first pushes the open past that window and Chrome blocks it
     * (which is why the picker used to need a second click). The caller therefore opens a
     * blank popup synchronously in its click handler and hands the handle over here.
     *
     * Ownership: once supplied, this service closes the window on every exit path. When the
     * handle is null or already closed, `openPicker` resolves `[]`. Omit it to keep the
     * legacy self-open behaviour.
     */
    targetWindow?: Window | null;
}

/**
 * Injectable seam. The container calls `openPicker(connectionId)` and awaits the user's
 * folder selection. Implementations MUST resolve with an empty array when the user cancels
 * (never reject on cancel).
 */
@Injectable()
export abstract class SharePointFilePickerService {
    public abstract openPicker(
        connectionId: string,
        options?: OpenPickerOptions,
    ): Promise<PickedSharepointFolder[]>;
}

/**
 * A `command` message payload from the picker. `command` is the command name; the
 * `authenticate` command additionally carries the `resource` (e.g.
 * `https://contoso.sharepoint.com`) and `type` (e.g. `SharePoint`) the token must be
 * scoped to, and `pick` carries the selected `items`.
 */
interface PickerCommand {
    command?: string;
    resource?: string;
    type?: string;
    items?: Array<Record<string, unknown>>;
}

/**
 * Real implementation: loads Microsoft's hosted OneDrive/SharePoint File Picker v8 into a
 * popup window and drives the documented postMessage/MessagePort handshake.
 *
 * This is LIVE-VERIFY only (see the top-of-file comment): the handshake can only be
 * exercised against a live tenant with the running app, so it is intentionally NOT unit
 * tested. The container is covered with the Mock implementation instead.
 *
 * Protocol (File Picker v8, per the Microsoft docs):
 *   1. Open a popup, then FORM-POST it to
 *      `{baseUrl}/_layouts/15/FilePicker.aspx?filePicker={json}` with a hidden
 *      `access_token` input carrying a token scoped to `{baseUrl}`.
 *   2. The picker posts an `initialize` message carrying our `channelId`; we take
 *      `event.ports[0]`, start it, and post `{ type: 'activate' }`.
 *   3. Every `command` message is ACKNOWLEDGED first
 *      (`{ type: 'acknowledge', id }`), then answered with a `result`.
 *   4. `authenticate` → mint a token for `command.resource` and reply
 *      `{ result: 'token', token }`. `pick` → reply `{ result: 'success' }` and resolve
 *      with the picked items. `close` → resolve `[]`.
 */
@Injectable()
export class DefaultSharePointFilePickerService extends SharePointFilePickerService {
    private static readonly PICKER_WINDOW_NAME = 'sharepoint-file-picker';
    /** The docs' recommended maximum picker size. */
    private static readonly PICKER_WINDOW_FEATURES = 'width=1080,height=680';

    public constructor(private readonly integrationService: IntegrationService) {
        super();
    }

    public async openPicker(
        connectionId: string,
        options?: OpenPickerOptions,
    ): Promise<PickedSharepointFolder[]> {
        const baseUrl = normalizeOrigin(options?.baseUrl);
        const includeSubfolders = options?.includeSubfolders ?? true;

        // REV-21057 BUG 2 — popup handoff. `targetWindow` present (even as null) means the
        // CALLER opened the popup synchronously inside its click handler; absent means we
        // self-open below, as before. When the caller owns the gesture but the browser still
        // refused the popup, there is nothing to drive.
        const callerOwnsWindow = options != null && 'targetWindow' in options;
        const suppliedWindow = options?.targetWindow ?? null;
        if (callerOwnsWindow && (suppliedWindow == null || suppliedWindow.closed)) {
            // eslint-disable-next-line no-console
            console.warn('[SharePointFilePicker] No usable popup window supplied — picker not opened.');
            return [];
        }
        /** Close a caller-supplied window on the early-exit paths so no blank popup is orphaned. */
        const closeSuppliedWindow = (): void => {
            try {
                suppliedWindow?.close();
            } catch {
                /* window may already be gone */
            }
        };

        // No tenant SPO host → no picker. There is NO fallback host on purpose: a
        // hardcoded default would point the picker at a Microsoft-owned tenant the user
        // has no business browsing. The host comes from the BE picker-token response.
        if (baseUrl == null) {
            // eslint-disable-next-line no-console
            console.warn(
                '[SharePointFilePicker] No SharePoint base URL available — cannot open the ' +
                    'file picker. The BE picker-token response must supply `baseUrl` ' +
                    '(the tenant SharePoint Online origin, e.g. https://contoso.sharepoint.com).',
            );
            closeSuppliedWindow();
            return [];
        }

        // The initial form-POST token must already be scoped to the picker host.
        const initialToken = await this.mintToken(connectionId, baseUrl);
        if (initialToken == null) {
            // eslint-disable-next-line no-console
            console.warn(
                `[SharePointFilePicker] Could not mint an access token for ${baseUrl} — picker not opened.`,
            );
            closeSuppliedWindow();
            return [];
        }

        const channelId = createChannelId();
        const params = {
            sdk: '8.0',
            messaging: {
                channelId,
                origin: window.location.origin,
            },
            // Presence of `authentication` (even empty) tells the picker the HOST supplies
            // tokens — required to get detailed item data back.
            authentication: {},
            entry: { sharePoint: {} },
            typesAndSources: {
                mode: 'folders',
                pivots: { oneDrive: true, sharedLibraries: true, site: true },
            },
            // AC B2: the user may add several closing folders in one pass.
            selection: { mode: 'multiple' },
        };

        return new Promise<PickedSharepointFolder[]>((resolve) => {
            // Reuse the caller's pre-opened window when it handed one over (the only path
            // that survives Chrome's popup blocker after an await); otherwise self-open.
            const pickerWindow = callerOwnsWindow
                ? suppliedWindow
                : window.open(
                      '',
                      DefaultSharePointFilePickerService.PICKER_WINDOW_NAME,
                      DefaultSharePointFilePickerService.PICKER_WINDOW_FEATURES,
                  );

            // Popup blocked: fail soft with no selection so the container just does nothing
            // (no folders added). A user-facing hint is out of scope for the seam.
            if (pickerWindow == null) {
                // eslint-disable-next-line no-console
                console.warn('[SharePointFilePicker] Popup blocked — picker not opened.');
                resolve([]);
                return;
            }

            let port: MessagePort | null = null;
            let settled = false;

            const finish = (folders: PickedSharepointFolder[]): void => {
                if (settled) return;
                settled = true;
                window.removeEventListener('message', onWindowMessage);
                try {
                    port?.close();
                } catch {
                    /* channel already torn down */
                }
                try {
                    pickerWindow.close();
                } catch {
                    /* cross-origin window may already be closed */
                }
                resolve(folders);
            };

            /** Step 2 — the picker announces itself on OUR channel; wire up the MessagePort. */
            const onWindowMessage = (event: MessageEvent): void => {
                if (event.source == null || event.source !== pickerWindow) return;

                const message = event.data as { type?: string; channelId?: string } | null;
                if (message == null || typeof message !== 'object') return;

                if (
                    message.type === 'initialize' &&
                    message.channelId === channelId &&
                    event.ports?.[0] != null
                ) {
                    port = event.ports[0];
                    port.addEventListener('message', onPortMessage);
                    port.start();
                    port.postMessage({ type: 'activate' });
                }
            };

            /** Steps 3 + 4 — acknowledge every command, then answer it. */
            const onPortMessage = (event: MessageEvent): void => {
                const payload = event.data as
                    | { type?: string; id?: string; data?: unknown }
                    | null;
                if (payload == null || typeof payload !== 'object') return;
                // Notifications need no response (`page-loaded` etc.).
                if (payload.type !== 'command') return;

                const id = payload.id;
                // ALL commands must be acknowledged, before the result.
                port?.postMessage({ type: 'acknowledge', id });

                const command = (payload.data ?? {}) as PickerCommand;

                switch (command.command) {
                    case 'authenticate': {
                        // The picker tells us WHICH resource the token must be scoped to.
                        const resource = command.resource ?? baseUrl;
                        void this.mintToken(connectionId, resource).then((token) => {
                            if (token == null) {
                                port?.postMessage({
                                    type: 'result',
                                    id,
                                    data: {
                                        result: 'error',
                                        error: {
                                            code: 'unableToObtainToken',
                                            message: `No token available for ${resource}`,
                                        },
                                    },
                                });
                                return;
                            }
                            port?.postMessage({
                                type: 'result',
                                id,
                                data: { result: 'token', token },
                            });
                        });
                        return;
                    }
                    case 'pick': {
                        port?.postMessage({
                            type: 'result',
                            id,
                            data: { result: 'success' },
                        });
                        finish(mapPickedItems(command.items ?? [], includeSubfolders));
                        return;
                    }
                    case 'close': {
                        finish([]);
                        return;
                    }
                    default: {
                        // Always send a reply, even if that reply is "unsupported", so the
                        // picker does not hang waiting on us.
                        port?.postMessage({
                            type: 'result',
                            id,
                            data: {
                                result: 'error',
                                error: {
                                    code: 'unsupportedCommand',
                                    message: command.command ?? 'unknown',
                                },
                            },
                        });
                        return;
                    }
                }
            };

            window.addEventListener('message', onWindowMessage);

            // Step 1 — FORM POST the popup to the picker page. The picker options travel in
            // the query string; the initial token travels as a hidden `access_token` input.
            const queryString = new URLSearchParams({
                filePicker: JSON.stringify(params),
            }).toString();
            const url = `${baseUrl}/_layouts/15/FilePicker.aspx?${queryString}`;

            try {
                const doc = pickerWindow.document;
                const form = doc.createElement('form');
                form.setAttribute('action', url);
                form.setAttribute('method', 'POST');

                const tokenInput = doc.createElement('input');
                tokenInput.setAttribute('type', 'hidden');
                tokenInput.setAttribute('name', 'access_token');
                tokenInput.setAttribute('value', initialToken);
                form.appendChild(tokenInput);

                doc.body.append(form);
                form.submit();
            } catch {
                // Cross-origin document access edge case: bail with no selection.
                finish([]);
            }
        });
    }

    /** BE-minted, resource-scoped access token. Resolves `null` on any failure. */
    private async mintToken(connectionId: string, resource: string): Promise<string | null> {
        try {
            const response = await firstValueFrom(
                this.integrationService.getSharepointPickerToken(connectionId, resource),
            );
            return response.token.length > 0 ? response.token : null;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(
                `[SharePointFilePicker] Failed to mint a token for ${resource}: ${String(error)}`,
            );
            return null;
        }
    }
}

/** A fresh GUID per picker instance, as the schema requires for `messaging.channelId`. */
function createChannelId(): string {
    const cryptoRef = window.crypto as Crypto & { randomUUID?: () => string };
    if (typeof cryptoRef?.randomUUID === 'function') {
        return cryptoRef.randomUUID();
    }
    // Fallback for browsers without crypto.randomUUID — uniqueness is all that matters.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.floor(Math.random() * 16);
        const v = c === 'x' ? r : (r % 4) + 8;
        return v.toString(16);
    });
}

/** Strip a trailing slash; return null for a missing/blank host. */
function normalizeOrigin(value: string | null | undefined): string | null {
    if (value == null) return null;
    const trimmed = value.trim().replace(/\/+$/, '');
    return trimmed.length > 0 ? trimmed : null;
}

/**
 * Map raw picker items → the container's `PickedSharepointFolder` shape.
 *
 * The picker returns Graph DriveItems: the drive the item lives in is on
 * `parentReference.driveId` (guaranteed present per the docs — a DriveItem has no
 * top-level `driveId`), and the folder's location is `parentReference.path`, a Graph path
 * of the form `/drives/{driveId}/root:/Closings/2026`. We strip that prefix and append the
 * item name so the persisted `folderPath` is the human-readable full path, not just the leaf.
 */
function mapPickedItems(
    items: Array<Record<string, unknown>>,
    includeSubfolders: boolean,
): PickedSharepointFolder[] {
    return items.map((item) => {
        const parentReference = (item['parentReference'] ?? {}) as {
            driveId?: unknown;
            path?: unknown;
        };
        const name = typeof item['name'] === 'string' ? item['name'] : '';

        return {
            driveId: String(parentReference.driveId ?? item['driveId'] ?? ''),
            folderItemId: String(item['id'] ?? item['folderItemId'] ?? ''),
            folderPath: buildFolderPath(parentReference.path, name, item['folderPath']),
            includeSubfolders,
            source: (item['source'] as 'sharepoint' | 'onedrive') ?? 'sharepoint',
        };
    });
}

/**
 * Build the fullest folder path we can from the picker item: parent path + item name,
 * falling back to the bare name (or a legacy `folderPath` field) when Graph gave us no
 * parent path.
 */
function buildFolderPath(rawParentPath: unknown, name: string, legacyPath: unknown): string {
    const fallback = name.length > 0 ? name : String(legacyPath ?? '');

    if (typeof rawParentPath !== 'string' || rawParentPath.length === 0) {
        return fallback;
    }

    // `/drives/{driveId}/root:` → '' so only the tenant-relative path remains.
    const parentPath = rawParentPath.replace(/^\/drives\/[^/]+\/root:/, '').replace(/\/+$/, '');
    if (name.length === 0) {
        return parentPath.length > 0 ? parentPath : fallback;
    }
    return parentPath.length > 0 ? `${parentPath}/${name}` : `/${name}`;
}

/**
 * Test/dev implementation: returns a deterministic fixture without touching
 * Microsoft. Provided in tests; the container's `onAddFolder` path is unit
 * tested against this.
 */
@Injectable()
export class MockSharePointFilePickerService extends SharePointFilePickerService {
    public async openPicker(
        _connectionId: string,
        options?: OpenPickerOptions,
    ): Promise<PickedSharepointFolder[]> {
        // The mock never navigates the popup, so it owns the same teardown contract as the
        // real implementation: a caller-supplied window is always closed (REV-21057 BUG 2).
        try {
            options?.targetWindow?.close();
        } catch {
            /* window may already be gone */
        }
        return [
            {
                driveId: 'mock-drive-1',
                folderItemId: 'mock-folder-1',
                folderPath: '/Closings/2026/Q1',
                includeSubfolders: options?.includeSubfolders ?? true,
                source: 'sharepoint',
            },
        ];
    }
}
